import { RadioGroup } from '@mui/material';
import { SurveyQuestionType } from 'posthog-js';
import { useState } from 'react';
import type { MultipleSurveyQuestion } from 'posthog-js';

import FormGroup from '../../Forms/FormGroup';
import LabeledRadio from '../../Forms/LabeledRadio';
import LabeledRadioWithInput from '../../Forms/LabeledRadioWithInput';

import type { QuestionRendererProps } from './registry';

type ContentProps = {
  question: MultipleSurveyQuestion;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
};

const SingleChoiceQuestionContent = ({ question, value, onChange }: ContentProps) => {
  const { choices, hasOpenChoice } = question;
  const regularChoices = hasOpenChoice ? choices.slice(0, -1) : choices;
  const openChoice = hasOpenChoice ? choices[choices.length - 1] : null;

  const stringValue = Array.isArray(value) ? '' : value ?? '';
  const isOpenFromValue = !!openChoice
    && stringValue !== ''
    && !regularChoices.includes(stringValue);

  const [isOpenSelected, setIsOpenSelected] = useState<boolean>(isOpenFromValue);
  const [stickyText, setStickyText] = useState<string>(isOpenFromValue ? stringValue : '');

  const radioValue = isOpenSelected ? openChoice ?? '' : stringValue;

  const handleRadioChange = (_event: unknown, choice: string) => {
    if (openChoice && choice === openChoice) {
      setIsOpenSelected(true);
      onChange(stickyText);
      return;
    }
    setIsOpenSelected(false);
    onChange(choice);
  };

  const handleTextChange = (text: string) => {
    setStickyText(text);
    onChange(text);
  };

  return (
    <RadioGroup value={radioValue} onChange={handleRadioChange}>
      <FormGroup legend={question.question} spacing="sm">
        {regularChoices.map((choice) => (
          <LabeledRadio key={choice} label={choice} value={choice} />
        ))}
        {openChoice && (
          <LabeledRadioWithInput
            key={openChoice}
            label={openChoice}
            value={openChoice}
            isChecked={isOpenSelected}
            inputValue={stickyText}
            onInputChange={handleTextChange}
          />
        )}
      </FormGroup>
    </RadioGroup>
  );
};

/**
 * Renders a PostHog single-choice question as a radio group. When the question
 * has `hasOpenChoice: true`, the last choice becomes a `LabeledRadioWithInput`
 * — selecting it reveals an inline text input whose value replaces the choice's
 * label in the captured answer. The typed value is "sticky": it survives
 * deselect/reselect within the same step lifetime.
 */
export const SingleChoiceQuestion = ({ question, value, onChange }: QuestionRendererProps) => {
  if (question.type !== SurveyQuestionType.SingleChoice) {
    return null;
  }
  return <SingleChoiceQuestionContent question={question} value={value} onChange={onChange} />;
};
